/* ============================================================================
   La Maison Eterna — scroll-reveal + parallax (vanilla, dependency-free).

   Replicates the reference framer-motion vocabulary with native APIs:
     • [data-reveal="fade-up|fade|fade-left|fade-right|line"]  → entrance reveal
     • [data-reveal-group]  on a container  → stagger its [data-reveal] children
         (per-item delay = index * (data-reveal-stagger || 60)ms, computed live)
     • [data-reveal-delay="120"]  → explicit delay in ms (overrides group stagger)
     • [data-parallax] [data-parallax-speed="0.12"] [data-parallax-scale="1"] → scroll parallax

   Hidden state is gated behind html.js-reveal (set synchronously in theme.liquid),
   so nothing is ever hidden if JS fails. Reveals fire once (then unobserve).
   Respects prefers-reduced-motion. Catches dynamically-appended cards via a
   MutationObserver (the collection page appends product cards after load).
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  if (root.dataset.animInit === '1') { return; }
  root.dataset.animInit = '1';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Reveal ──────────────────────────────────────────────────────────── */

  function clearWillChange(el) {
    // Drop the GPU layer once the reveal transition finishes — keeping
    // will-change on dozens of revealed elements degrades mobile scroll.
    var done = function (e) {
      if (e && e.target !== el) { return; }
      el.style.willChange = 'auto';
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    setTimeout(done, 1400); // fallback if transitionend doesn't fire
  }

  function revealNow(el) {
    el.classList.add('is-revealed');
    clearWillChange(el);
  }

  if (reduce || !('IntersectionObserver' in window)) {
    // No animation: mark everything revealed + drop will-change (CSS forces final state).
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-revealed');
      el.style.willChange = 'auto';
    });
  } else {
    // Cap so a card never waits more than MAX_STAGGER_STEPS * step ms.
    var MAX_STAGGER_STEPS = 5; // 5 * 60ms default = 300ms max
    var io = new IntersectionObserver(function (entries) {
      // Stagger is scoped to THIS callback ("batch") and reset each time, keyed by
      // reveal-group: cards that enter together cascade; a card that enters alone
      // (the common case while scrolling) gets index 0 and reveals immediately.
      var batchCount = new Map();
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) { return; }
        var el = entry.target;
        var delay = el.getAttribute('data-reveal-delay');
        if (delay == null) {
          var group = el.closest('[data-reveal-group]');
          if (group) {
            var step = parseInt(group.getAttribute('data-reveal-stagger'), 10) || 60;
            var n = batchCount.get(group) || 0;
            batchCount.set(group, n + 1);
            delay = Math.min(n, MAX_STAGGER_STEPS) * step;
          } else {
            delay = 0;
          }
        }
        el.style.setProperty('--reveal-delay', delay + 'ms');
        revealNow(el);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0 });

    var observeReveals = function (scope) {
      var nodes = (scope || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
      nodes.forEach(function (el) { io.observe(el); });
    };

    observeReveals(document);

    // Catch product cards appended after load (collection page auto-loads pages).
    var watchTargets = document.querySelectorAll('[data-collection-grid], [data-reveal-watch]');
    if (watchTargets.length && 'MutationObserver' in window) {
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          for (var i = 0; i < m.addedNodes.length; i++) {
            var node = m.addedNodes[i];
            if (node.nodeType !== 1) { continue; }
            if (node.matches && node.matches('[data-reveal]')) { io.observe(node); }
            if (node.querySelectorAll) { observeReveals(node); }
          }
        });
      });
      watchTargets.forEach(function (t) { mo.observe(t, { childList: true }); });
    }
  }

  /* ── Parallax ────────────────────────────────────────────────────────── */

  if (!reduce) {
    var parallaxEls = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
    if (parallaxEls.length) {
      var ticking = false;

      var update = function () {
        ticking = false;
        var vh = window.innerHeight || document.documentElement.clientHeight;
        for (var i = 0; i < parallaxEls.length; i++) {
          var el = parallaxEls[i];
          var rect = el.getBoundingClientRect();
          if (rect.bottom < -vh || rect.top > vh * 2) { continue; } // far off-screen
          var speed = parseFloat(el.getAttribute('data-parallax-speed')) || 0.12;
          var scale = el.getAttribute('data-parallax-scale');
          // Offset of the element's centre from the viewport centre.
          var fromCenter = (rect.top + rect.height / 2) - vh / 2;
          var shift = -(fromCenter * speed);
          el.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0)' + (scale ? ' scale(' + scale + ')' : '');
        }
      };

      var onScroll = function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }
  }
})();
